// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import { Permission } from '@/authorization/types';
import {
  parseTimeDataUpdateErrorResponse,
  uploadTimeDataUpdate,
} from '@/costs/api/upload-time-data-update';
import { CsvImportFlow } from '@/csv-import/components/csv-import-flow';
import type { CsvImportTemplateColumn } from '@/csv-import/types';
import { buildUpdateResultsView } from '@/csv-import/update-results-view';
import { Alert, AlertTitle, Box } from '@mui/material';
import { useMemo } from 'react';

const KEY_COLUMN_HEADER = 'Hardfin time ID';

interface TimeUpdateColumn extends CsvImportTemplateColumn {
  isRateColumn?: boolean;
}

const COLUMNS_HINT =
  'Every included column must have a value in each row, except where noted';

const BLANK_CLEARS_PROJECT = 'Blank clears the project';
const BLANK_CLEARS_RATE = 'Blank clears the rate';

const TIME_UPDATE_COLUMNS: TimeUpdateColumn[] = [
  {
    header: KEY_COLUMN_HEADER,
    description: 'Copy from the time export',
    alwaysIncluded: true,
  },
  {
    header: 'User email',
    description: 'Login email of an organization member',
  },
  {
    header: 'Date',
    description: 'Day the time was incurred, today or earlier (2026-03-25)',
  },
  {
    header: 'Hours',
    description: 'Whole hours worked, 0 to 24',
    children: [
      {
        header: 'Minutes',
        description: 'Whole minutes worked, 0 to 60',
      },
    ],
  },
  {
    header: 'Category',
    description: 'Category label',
  },
  {
    header: 'Description',
    description: 'Text note about this time record',
  },
  {
    header: 'Project',
    description: 'Project name',
    blankNote: BLANK_CLEARS_PROJECT,
  },
  {
    header: 'Hourly rate',
    description: 'Rate amount, such as 55.25',
    blankNote: BLANK_CLEARS_RATE,
    isRateColumn: true,
    children: [
      {
        header: 'Currency',
        description: 'ISO code for the rate, such as USD',
      },
    ],
  },
];

const TIME_RECORD_NOUN = { singular: 'time record', plural: 'time records' };

interface TimeDataUpdateUploadProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export function TimeDataUpdateUpload(props: TimeDataUpdateUploadProps) {
  const { hasPermission } = usePermissionChecker();
  // The rate field is guarded by time.rate:create everywhere it is written,
  // so the importer gate and this check both use the create action
  const ratePermission = hasPermission('time.rate', 'create');
  const canSetRate =
    ratePermission === Permission.PermissionExplicitTrue ||
    ratePermission === Permission.PermissionImplicitTrue;

  // Rate columns are stripped before the template step so restricted users
  // never see them as checkboxes or template headers
  const columns = useMemo(() => {
    const visible = canSetRate
      ? TIME_UPDATE_COLUMNS
      : TIME_UPDATE_COLUMNS.filter((column) => !column.isRateColumn);
    return visible.map((column) => ({
      header: column.header,
      description: column.description,
      alwaysIncluded: column.alwaysIncluded,
      restricted: column.isRateColumn,
      blankNote: column.blankNote,
      children: column.children,
    }));
  }, [canSetRate]);

  return (
    <CsvImportFlow
      templateStep={{
        title: 'Template for updating time data',
        columns,
        columnsHint: COLUMNS_HINT,
        infoAlert: (
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Important information about the template</AlertTitle>
            <Box component="ul" sx={{ pl: 3, mt: 1 }}>
              <li>
                This uploader <u>updates existing records</u>; it does not
                create them
              </li>
              <li>Hardfin time ID must match an existing time record</li>
              <li>Only the columns included in the upload file are updated</li>
              <li>Hours and minutes together cannot exceed 24 hours</li>
            </Box>
          </Alert>
        ),
        fileNamePrefix: 'time_data_update_template',
      }}
      uploadStep={{
        dropZoneTitle: 'Upload a CSV file with your time data updates',
        recordNoun: TIME_RECORD_NOUN,
        warningAlert: (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Warning about time data updates</AlertTitle>
            <Box
              component="ul"
              sx={{ pl: 3, mt: 2, mb: 1, '& li + li': { mt: 0.5 } }}
            >
              <li>This upload updates existing time records only</li>
              <li>
                Blank project{canSetRate ? ' and hourly rate' : ''} cells clear
                those fields
              </li>
              <li>Delete a column to leave that field unchanged</li>
              <li>No changes are applied unless every row is valid</li>
            </Box>
          </Alert>
        ),
      }}
      uploadFile={uploadTimeDataUpdate}
      parseErrorResponse={parseTimeDataUpdateErrorResponse}
      supportErrorMessage="An error occurred while uploading time data."
      buildResultsView={(result) =>
        buildUpdateResultsView(result, {
          keyColumnHeader: KEY_COLUMN_HEADER,
          recordNoun: TIME_RECORD_NOUN,
          dataLabel: 'time data',
        })
      }
      onSuccess={props.onSuccess}
      onClose={props.onClose}
    />
  );
}
